import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import styles from "./styles.module.css";
import HomeHeader from "../../components/HomeHeader";
import ImageComponent from "../../components/ImageComponent";
import Accordion from "../../components/Accordion";
import AccordionItem from "../../components/Accordion/AccordionItem";
import Skeleton from "../../components/Skeleton";
import { useTranslation } from "react-i18next";
import TVIcon from "../../assets/play-icon.svg";
import MahaMegaTournamentCard from "../../components/MahaMegaTournamentCard";
import PromotionBanner from "../../components/PromotionBanner";
import { useTournamentsListApi } from "../../api/useTournamentsListApi";
import CardComponent from "../../components/CardComponent";
import TournamentCard from "../../components/TournamentCards";
import { Widget } from "../../interfaces/TournamentsConfig";
import { WIDGET_TYPES_NX } from "../../utils/helpers/tournamentConstants";
import TournamentHeader from "../../components/TournamentHeader/TournamentHeader";
import BackToTopIcon from "../../components/BackToTopIcon";
import noTournamentIcon from "../../assets/no-tournaments-available.svg";
import technicalErrorIcon from "../../assets/technical-error.svg";
import Loader from "../../components/Loader";
import { _isEmpty } from "../../utils/helpers/zupeeLodash";
import { CLEVERTAP_EVENTS, NAVIGATION_ROUTES } from "../../constant";
import { getPageName, getPartnerId } from "../../utils/helpers/common";
import { pushClevertapEvent } from "../../utils/analytics/clevertap";
import { useTournamentNavigation } from "../../utils/helpers/tournamentNavigationHelper";
import { FbPixelEvent_PageView } from "../../utils/FbPixel/fbPixel";
import { FACEBOOK } from "../../utils/helpers/constants";

const Home: React.FC = () => {
  const topRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const { tournamentsConfig, isLoading, error } = useTournamentsListApi();
  const [gameDetails, setGameDetails] = useState<any>(null);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [isRunGameplay, setIsRunGameplay] = useState<boolean>(false);
  const [isGameStarting, setIsGameStarting] = useState<boolean>(false);
  const {handleNavigation} = useTournamentNavigation({ gameName: gameDetails?.gameName, ltid: gameDetails?.ltid });
  useEffect(() => {
    const gameDetails = localStorage.getItem('gameDetails');
    if (gameDetails) {
      setGameDetails(JSON.parse(gameDetails));
      setIsLoggedIn(localStorage.getItem('isLoggedIn') === 'true');
      setIsRunGameplay(localStorage.getItem('runGameplay') === 'true');
    }
  }, []);

  const hasTrackedRef = useRef(false);
  
  useEffect(() => {
    if (getPartnerId() === FACEBOOK && !hasTrackedRef.current) {
      hasTrackedRef.current = true;
      FbPixelEvent_PageView();
    }
  }, []);

  useEffect(() => {
    if(!isLoggedIn){
      localStorage.removeItem('runGameplay');
      localStorage.removeItem('gameDetails');
    }
    if (gameDetails && isRunGameplay && isLoggedIn) {
      setIsGameStarting(true);
      localStorage.removeItem('runGameplay');
      localStorage.removeItem('gameDetails');
      localStorage.removeItem('isLoggedIn');
      handleNavigation();
    }
  }, [gameDetails, handleNavigation]);

  const errorData = error instanceof Error ?
    (() => {
      try {
        return JSON.parse(error.message) as { type: string; message: string };
      } catch {
        return { type: "ERROR", message: error.message };
      }
    })()
    : null;

  const isUserBlocked = errorData?.type === "USER_BLOCKED";
  const errorMessage = errorData?.message || "";
  const SKELETON_TIMEOUT = 100;

  const lobbyMainBanner = tournamentsConfig?.gameDetails?.[0]?.coverImageV2;
  // const gameDescription = tournamentsConfig?.gameDetails?.[0]?.description;

  // Memoized list of widgets
  const widgetList = useMemo(
    () => tournamentsConfig?.lobby?.tournamentsList?.widgets || [],
    [tournamentsConfig]
  );

  const findFirstMegaTournamentIndex = () => {
    return widgetList.findIndex((widget) => widget.widgetType === WIDGET_TYPES_NX.PLAY_WITH_MONEY_NX && !widget.isCollapsable)
  }

  const findFirstMahaMegaTournamentIndex = () => {
    return widgetList.findIndex((widget) => widget.widgetType === WIDGET_TYPES_NX.MAHA_MEGA_TOURNAMENT && !widget.isCollapsable)
  }

  // Collapsible widget data
  const collapsibleData = useCallback(
    () => widgetList.filter((widget) => widget?.isCollapsable),
    [widgetList]
  );

  // CT Page View
  useEffect(() => {
    const trackingTimeout = setTimeout(() => {
      pushClevertapEvent(CLEVERTAP_EVENTS.EVENT_PWA_PAGE_VIEW_HOME, {
        "pageName": getPageName(),
        "partnerId": getPartnerId(),
      });
    }, 100);
    return () => clearTimeout(trackingTimeout);
  }, [getPartnerId()]);

  // Simulate loading effect
  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), SKELETON_TIMEOUT);
    return () => clearTimeout(timer);
  }, []);

  const scrollToTop = () =>
    topRef.current?.scrollIntoView({ behavior: "smooth" });

  // Render widget content dynamically
  const renderWidgetContent = (widget: Widget, index: number) => {
    const { widgetType, items, pwaBanner, recentlyConcludedTable } = widget;
    if (loading) return <Skeleton key={index} variant="card" />;

    switch (widgetType) {
      case WIDGET_TYPES_NX.PWA_BANNER:
        return pwaBanner && <PromotionBanner key={index} {...pwaBanner} />;
      case WIDGET_TYPES_NX.LR_HEADER:
        return recentlyConcludedTable ? (
          <Accordion key={index}>
            <AccordionItem
              accordionHeaderClassName={clsx(styles.recentlyConcludedTournamentsHeader)}
              title={t("recentlyConcludedTournaments")}
              accordionInnerContentClassName={clsx(
                styles.recentlyConcludedTournaments
              )}
            >
              {collapsibleData().map((collapsibleWidget, idx) => {
                switch (collapsibleWidget?.widgetType) {
                  case WIDGET_TYPES_NX.MAHA_MEGA_TOURNAMENT:
                    return (
                      <MahaMegaTournamentCard
                        key={idx}
                        items={collapsibleWidget.items}
                        isCollapsable
                      />
                    );
                  case WIDGET_TYPES_NX.PLAY_WITH_MONEY_NX:
                    return (
                      <TournamentCard
                        key={idx}
                        items={collapsibleWidget.items}
                        isCollapsable
                      />
                    );
                }
              })}
            </AccordionItem>
          </Accordion>
        ) : (
          <TournamentHeader
            key={index}
            items={items}
            wrapperStyle={styles.tournamentHeading}
          />
        );
      case WIDGET_TYPES_NX.PLAY_WITH_MONEY_NX: {
        const firstMegaTournamentIndex = findFirstMegaTournamentIndex();
        return (
          !widget.isCollapsable && <TournamentCard key={index} items={items} widgetIndex={index} firstMegaTournamentIndex={firstMegaTournamentIndex} />
        );
      }

      case WIDGET_TYPES_NX.MAHA_MEGA_TOURNAMENT:{
        const firstMahaMegaTournamentIndex = findFirstMahaMegaTournamentIndex();
        return (
          !widget.isCollapsable && (
            <MahaMegaTournamentCard key={index} items={items} widgetIndex={index} firstMahaMegaTournamentIndex={firstMahaMegaTournamentIndex}/>
          )
        );
      }
       
      case WIDGET_TYPES_NX.IMAGE:
        return (
          <BackToTopIcon key={index} onClick={scrollToTop} items={items} />
        );
      default:
        return null;
    }
  };
  const navigateToHowToPlayPage = () => {
    pushClevertapEvent(CLEVERTAP_EVENTS.EVENT_PWA_HTP_VIDEO, {
      "pageName": getPageName(),
    });

    navigate(NAVIGATION_ROUTES.HOW_TO_PLAY);
  };
  
  const renderErrorCard = () => {
    if (isUserBlocked) {
      return (
        <CardComponent className={clsx(styles.emptyStateCard)}>
          <div className={clsx("textAlignCenter")}>
            <ImageComponent
              src={technicalErrorIcon}
              alt="blocked"
              className={clsx(styles.noTournamentIcon)}
              style={{ marginBottom: "20px" }}
            />
            <h4 className={clsx("textAlignCenter", "fontColorError")}>
              {errorMessage}
            </h4>
          </div>
        </CardComponent>
      );
    }

    return (
      <CardComponent className={clsx(styles.emptyStateCard)}>
        <div className={clsx("textAlignCenter")}>
          <ImageComponent
            src={noTournamentIcon}
            alt="no tournament"
            className={clsx(styles.noTournamentIcon)}
            style={{ marginBottom: "20px" }}
          />
          <h4 className={clsx("textAlignCenter")}>
            {t("noTournamentsFound")}
          </h4>
        </div>
      </CardComponent>
    );
  };

  if (isLoading || isGameStarting) {
    return (
      <>
      <Loader transparent />
    </>
    );
  }
  return (
    <>

    <div
      className={clsx(styles.pageWrapper, styles.home, "pageWrapper noHeader")}
      ref={topRef}
    >
      {/* Cover Banner */}
      <div
        className={clsx(styles.coverBannerBlock)}
        style={{ backgroundImage: `url(${lobbyMainBanner})` }}
      >
        <HomeHeader tournamentsConfig={tournamentsConfig} />
        <div className={clsx(styles.ludoTvContainer)}>
          <div className={clsx(styles.ludoTvRedirectionLink)}>
            <div
              className={clsx(styles.iconWrapper)}
              onClick={navigateToHowToPlayPage}
            >
              <ImageComponent
                src={TVIcon}
                alt="TV Icon"
                className={clsx(styles.ludoTvIcon)}
              />
            </div>
          </div>
        </div>
        {/* <div
          className={clsx(
            styles.movesText,
            "fontColorWhite fontSizeXs fontWeightRegular"
          )}
        >
          {isLoading ? <Skeleton variant="text" /> : gameDescription}
        </div> */}
      </div>
      {_isEmpty(widgetList) ? renderErrorCard() : (
        <div className={clsx(styles.tournamentListingContainer)}>
          <div className={clsx(styles.innerContentBox)}>
            <div className={clsx(styles.tournamentContainer)}>
              {widgetList.map((widget, index) =>
                renderWidgetContent(widget, index)
              )}
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default Home;
